import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Button, 
  Grid, 
  Column, 
  Tile, 
  Tag, 
  Select, 
  SelectItem, 
  DatePicker, 
  DatePickerInput,
  TimePicker,
  TimePickerSelect,
  Loading,
  InlineNotification,
  Modal,
  TextInput,
  Checkbox
} from '@carbon/react';
import { ArrowLeft, Temperature, Humidity, QrCode, Send, Download, Copy, Checkmark } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useSensorsOptimized } from '../hooks/useSensorsOptimized';
import { useVerifyReports } from '../hooks/useVerifyReports';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { generateAccessCode } from '../utils/generateAccessCode';
import SensorChart from '../components/charts/SensorChart';
import {
  REPORT_CHART_COLORS,
  inferReportTimeRange,
  buildReportChartSeries,
  exportReportChartCsv,
} from '../utils/reportChartUtils';
import ReportQRCode from '../components/ReportQRCode';

const VerifyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { sensors } = useSensorsOptimized();
  const { createReport, updateReport, logVisit } = useVerifyReports();
  const { user } = useAuth();
  const isNewReport = id === 'new';
  
  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  // Form state for new/edit report
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startTime, setStartTime] = useState('12:00');
  const [endTime, setEndTime] = useState('11:59');
  const [startMeridiem, setStartMeridiem] = useState('AM');
  const [endMeridiem, setEndMeridiem] = useState('PM');
  const [reportDescription, setReportDescription] = useState('');
  const [enableAccessCode, setEnableAccessCode] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  
  // Report state
  const [report, setReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(!isNewReport);
  const [copied, setCopied] = useState(false);
  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [isEditingRange, setIsEditingRange] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);

  // Fetch report data if viewing existing report
  useEffect(() => {
    if (isNewReport) return;

    const fetchReport = async () => {
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('verify_reports')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        setReport(data);

        // Fetch sensor readings for the date range
        if (data.sensor_id && data.start_date && data.end_date) {
          const { data: readings, error: readingsError } = await supabase
            .from('sensor_readings')
            .select('*')
            .eq('sensor_id', data.sensor_id)
            .gte('recorded_at', data.start_date)
            .lte('recorded_at', data.end_date)
            .order('recorded_at', { ascending: true });

          if (!readingsError) {
            setReportData(readings);
          }
        }
      } catch (err) {
        console.error('Error fetching report:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();
  }, [id, isNewReport]);

  // Generate unique report ID
  const generateReportId = () => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  // Generate report
  const handleGenerateReport = async () => {
    if (!selectedDeviceId || !startDate || !endDate) {
      setGenerateError('Please select a device and date/time range');
      return;
    }

    const combinedStart = combineDateAndTime(startDate, startTime, startMeridiem);
    const combinedEnd = combineDateAndTime(endDate, endTime, endMeridiem);

    if (combinedStart > combinedEnd) {
      setGenerateError('Start date/time must be before end date/time');
      return;
    }

    try {
      setIsGenerating(true);
      setGenerateError(null);

      // Fetch sensor readings
      const { data: readings, error: readingsError } = await supabase
        .from('sensor_readings')
        .select('*')
        .eq('sensor_id', selectedDeviceId)
        .gte('recorded_at', combinedStart.toISOString())
        .lte('recorded_at', combinedEnd.toISOString())
        .order('recorded_at', { ascending: true });

      if (readingsError) throw readingsError;

      const hasTemperature = readings.some(r => r.temperature !== null && r.temperature !== undefined);
      const hasHumidity = readings.some(r => r.humidity !== null && r.humidity !== undefined && r.humidity > 0);

      // Generate unique public ID
      const publicId = generateReportId();
      // Always derive the base URL from the current origin so it works
      // for any Netlify domain (e.g. chillverify.com, chillverify.com.au, etc.)
      const publicUrl = `${window.location.origin}/verify/${publicId}`;

      // Generate access code if enabled
      const accessCode = enableAccessCode ? generateAccessCode() : null;

      // Create report
      const newReport = await createReport({
        sensor_id: selectedDeviceId,
        start_date: combinedStart.toISOString(),
        end_date: combinedEnd.toISOString(),
        public_id: publicId,
        public_url: publicUrl,
        has_temperature: hasTemperature,
        has_humidity: hasHumidity,
        visit_count: 0,
        description: reportDescription?.trim() || null,
        access_code: accessCode,
      });

      // Generate and store QR code in Supabase Storage
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const { data: functionData, error: functionError } = await supabase.functions.invoke('generate-qr-code', {
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
            body: {
              reportUrl: publicUrl,
              reportId: newReport.id,
            },
          });

          if (!functionError && functionData?.qrCodeUrl) {
            // Update report with QR code URL
            await supabase
              .from('verify_reports')
              .update({ qr_code_url: functionData.qrCodeUrl })
              .eq('id', newReport.id);
            
            // Update local state
            newReport.qr_code_url = functionData.qrCodeUrl;
          } else if (functionError) {
            console.warn('QR code generation error:', functionError);
          }
        }
      } catch (qrError) {
        console.warn('Failed to generate QR code (will use fallback):', qrError);
        // Don't block report creation if QR generation fails
      }

      setReport(newReport);
      setReportData(readings);
      navigate(`/dashboard/verify/${newReport.id}`);
    } catch (err) {
      console.error('Error generating report:', err);
      setGenerateError(err.message || 'Failed to generate report');
    } finally {
      setIsGenerating(false);
    }
  };

  // Copy URL to clipboard
  const handleCopyUrl = async () => {
    if (!report?.public_url) return;
    try {
      await navigator.clipboard.writeText(report.public_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  // Send email with report link
  const handleSendEmail = async () => {
    if (!resendEmail || !report?.public_url) {
      setEmailError('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(resendEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    try {
      setIsSendingEmail(true);
      setEmailError(null);
      setEmailSuccess(false);

      // Use Supabase Edge Function
      console.log('📧 Sending email via edge function:', {
        email: resendEmail,
        reportUrl: report.public_url,
        hasQrCode: !!report.qr_code_url,
        hasAccessCode: !!report.access_code,
      });

      const { data: functionData, error: functionError } = await supabase.functions.invoke('send-verify-report-email', {
        body: {
          email: resendEmail,
          reportUrl: report.public_url,
          deviceName: selectedSensor?.name || 'Unknown Device',
          startDate: report.start_date,
          endDate: report.end_date,
          qrCodeUrl: report.qr_code_url || null,
          accessCode: report.access_code || null,
        },
      });

      console.log('📧 Email function response:', { functionData, functionError });

      if (functionError) {
        console.error('❌ Email function error:', functionError);
        throw new Error(functionError.message || 'Failed to send email');
      }

      // Check if the response indicates success
      if (functionData?.error) {
        console.error('❌ Email service error:', functionData.error);
        throw new Error(functionData.error.message || 'Email service returned an error');
      }

      if (!functionData?.success && !functionData?.data) {
        console.error('❌ Unexpected response format:', functionData);
        throw new Error('Unexpected response from email service');
      }

      console.log('✅ Email sent successfully');
      setEmailSuccess(true);
      setTimeout(() => {
        setResendModalOpen(false);
        setResendEmail('');
        setEmailSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Error sending email:', err);
      setEmailError(err.message || 'Failed to send email. Please try again.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Reset email form when modal opens/closes
  useEffect(() => {
    if (resendModalOpen && user?.email) {
      setResendEmail(user.email);
    } else if (!resendModalOpen) {
      setResendEmail('');
      setEmailError(null);
      setEmailSuccess(false);
    }
  }, [resendModalOpen, user]);

  // Helper: combine date + time (HH:mm) into a Date
  const combineDateAndTime = (date, timeString, meridiem) => {
    if (!date || !timeString) return date;
    const [hoursStr, minutesStr] = timeString.split(':');
    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return date;
    // Convert 12h + AM/PM to 24h
    const upperMeridiem = (meridiem || 'AM').toUpperCase();
    if (upperMeridiem === 'PM' && hours < 12) {
      hours += 12;
    } else if (upperMeridiem === 'AM' && hours === 12) {
      hours = 0;
    }
    const d = new Date(date);
    d.setHours(hours, minutes, 0, 0);
    return d;
  };

  // When entering edit mode, prefill date & time range from existing report
  useEffect(() => {
    if (report && isEditingRange) {
      try {
        setSelectedDeviceId(report.sensor_id);
        const start = report.start_date ? new Date(report.start_date) : null;
        const end = report.end_date ? new Date(report.end_date) : null;
        setStartDate(start);
        setEndDate(end);

        if (start) {
          const h = start.getHours().toString().padStart(2, '0');
          const m = start.getMinutes().toString().padStart(2, '0');
          setStartTime(`${h}:${m}`);
        }
        if (end) {
          const h = end.getHours().toString().padStart(2, '0');
          const m = end.getMinutes().toString().padStart(2, '0');
          setEndTime(`${h}:${m}`);
        }
        setReportDescription(report.description || '');
        setEnableAccessCode(!!report.access_code);
      } catch {
        // Ignore parse errors and leave as-is
      }
    }
  }, [report, isEditingRange]);

  // Update existing report range and regenerate charts
  const handleUpdateReportRange = async () => {
    if (!report || !selectedDeviceId || !startDate || !endDate) {
      setGenerateError('Please select a device and date/time range');
      return;
    }

    const combinedStart = combineDateAndTime(startDate, startTime, startMeridiem);
    const combinedEnd = combineDateAndTime(endDate, endTime, endMeridiem);

    if (combinedStart > combinedEnd) {
      setGenerateError('Start date/time must be before end date/time');
      return;
    }

    try {
      setIsGenerating(true);
      setGenerateError(null);

      // Fetch sensor readings for new range
      const { data: readings, error: readingsError } = await supabase
        .from('sensor_readings')
        .select('*')
        .eq('sensor_id', selectedDeviceId)
        .gte('recorded_at', combinedStart.toISOString())
        .lte('recorded_at', combinedEnd.toISOString())
        .order('recorded_at', { ascending: true });

      if (readingsError) throw readingsError;

      const hasTemperature = readings.some(
        (r) => r.temperature !== null && r.temperature !== undefined
      );
      const hasHumidity = readings.some(
        (r) => r.humidity !== null && r.humidity !== undefined && r.humidity > 0
      );

      // Handle access code: if enabled and report doesn't have one, generate it; if disabled, set to null
      let accessCodeUpdate = {};
      if (enableAccessCode && !report.access_code) {
        accessCodeUpdate.access_code = generateAccessCode();
      } else if (!enableAccessCode) {
        accessCodeUpdate.access_code = null;
      }
      // If enableAccessCode is true and report already has a code, keep it unchanged

      // Update report record
      const { data: updatedReports, error: updateError } = await supabase
        .from('verify_reports')
        .update({
          sensor_id: selectedDeviceId,
          start_date: combinedStart.toISOString(),
          end_date: combinedEnd.toISOString(),
          description: reportDescription?.trim() || null,
          has_temperature: hasTemperature,
          has_humidity: hasHumidity,
          ...accessCodeUpdate,
        })
        .eq('id', report.id)
        .select('*');

      if (updateError) throw updateError;

      const updated = updatedReports?.[0] || report;

      // Optionally regenerate QR code with same public URL
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session) {
          const { data: functionData, error: functionError } = await supabase.functions.invoke(
            'generate-qr-code',
            {
              headers: {
                Authorization: `Bearer ${session.access_token}`,
              },
              body: {
                reportUrl: updated.public_url,
                reportId: updated.id,
              },
            }
          );

          if (!functionError && functionData?.qrCodeUrl) {
            updated.qr_code_url = functionData.qrCodeUrl;
          } else if (functionError) {
            console.warn('QR code regeneration error:', functionError);
          }
        }
      } catch (qrError) {
        console.warn('Failed to regenerate QR code (non-blocking):', qrError);
      }

      setReport(updated);
      setReportData(readings);
      setIsEditingRange(false);
    } catch (err) {
      console.error('Error updating report:', err);
      setGenerateError(err.message || 'Failed to update report');
    } finally {
      setIsGenerating(false);
    }
  };

  const selectedSensor = sensors.find(s => s.id === (report?.sensor_id || selectedDeviceId));
  const tempUnit = selectedSensor?.temperature_unit || 'celsius';
  const tempUnitLabel = tempUnit === 'fahrenheit' ? '°F' : '°C';

  // Prepare chart data
  const chartData = useMemo(
    () => buildReportChartSeries(reportData, { tempUnit }),
    [reportData, tempUnit]
  );

  const reportTimeRange = useMemo(
    () => inferReportTimeRange(report?.start_date || startDate, report?.end_date || endDate),
    [report?.start_date, report?.end_date, startDate, endDate]
  );

  // Summary statistics for high / low / average
  const stats = useMemo(() => {
    if (!reportData || reportData.length === 0) {
      return {
        tempMin: null,
        tempMax: null,
        tempAvg: null,
        humidityMin: null,
        humidityMax: null,
        humidityAvg: null,
      };
    }

    let tempMin = null;
    let tempMax = null;
    let tempSum = 0;
    let tempCount = 0;

    let humidityMin = null;
    let humidityMax = null;
    let humiditySum = 0;
    let humidityCount = 0;

    reportData.forEach(reading => {
      if (reading.temperature !== null && reading.temperature !== undefined) {
        const t = reading.temperature;
        tempMin = tempMin === null ? t : Math.min(tempMin, t);
        tempMax = tempMax === null ? t : Math.max(tempMax, t);
        tempSum += t;
        tempCount += 1;
      }
      if (reading.humidity !== null && reading.humidity !== undefined && reading.humidity > 0) {
        const h = reading.humidity;
        humidityMin = humidityMin === null ? h : Math.min(h, humidityMin);
        humidityMax = humidityMax === null ? h : Math.max(h, humidityMax);
        humiditySum += h;
        humidityCount += 1;
      }
    });

    return {
      tempMin,
      tempMax,
      tempAvg: tempCount > 0 ? tempSum / tempCount : null,
      humidityMin,
      humidityMax,
      humidityAvg: humidityCount > 0 ? humiditySum / humidityCount : null,
    };
  }, [reportData]);

  if (isLoading) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loading description="Loading report..." />
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '16px 0' }}>
      <Grid>
        <Column lg={16} md={8} sm={4}>
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', padding: '0 16px' }}>
            <Button
              kind="ghost"
              renderIcon={ArrowLeft}
              onClick={() => navigate('/dashboard/verify')}
              size="md"
            >
              Back to Reports
            </Button>
            {!isNewReport && report && (
              <Button
                kind={isEditingRange ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setIsEditingRange((prev) => !prev)}
              >
                {isEditingRange ? 'Cancel Edit' : 'Edit Report Range'}
              </Button>
            )}
          </div>

          {isNewReport ? (
            // New Report Form
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '16px', margin: '0 16px' }}>
              <h2 style={{ color: textPrimary, marginBottom: '16px', fontSize: 'clamp(1.125rem, 4vw, 1.5rem)' }}>Generate New Report</h2>
              
              {generateError && (
                <InlineNotification
                  kind="error"
                  title="Error"
                  subtitle={generateError}
                  style={{ marginBottom: '24px' }}
                />
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Select
                  id="device-select"
                  labelText="Select Device"
                  value={selectedDeviceId}
                  onChange={(e) => setSelectedDeviceId(e.target.value)}
                  size="md"
                >
                  <SelectItem value="" text="Choose a device" />
                  {sensors.map(sensor => (
                    <SelectItem key={sensor.id} value={sensor.id} text={sensor.name} />
                  ))}
                </Select>

                {/* Optional device summary card for selected device */}
                {selectedSensor && (
                  <Tile
                    style={{
                      backgroundColor: bgSecondary,
                      border: `1px solid ${borderColor}`,
                      padding: '12px',
                    }}
                  >
                    <h3 style={{ color: textPrimary, margin: 0, marginBottom: '4px', fontSize: '0.875rem' }}>
                      {selectedSensor.name}
                    </h3>
                    <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>
                      {selectedSensor.zone_name || 'Unassigned zone'}
                    </p>
                    <p style={{ color: textSecondary, fontSize: '0.75rem', margin: '4px 0 0' }}>
                      Latest reading:{' '}
                      {selectedSensor.latest_reading
                        ? `${selectedSensor.latest_reading.temperature?.toFixed?.(1) ?? '--'}°C, ${
                            selectedSensor.latest_reading.humidity ?? '--'
                          }%`
                        : 'No recent data'}
                    </p>
                  </Tile>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                  <div>
                    <DatePicker
                      datePickerType="range"
                      onChange={(dates) => {
                        setStartDate(dates[0] || null);
                        setEndDate(dates[1] || null);
                      }}
                    >
                      <DatePickerInput
                        id="date-picker-input-id-start"
                        labelText="Start date"
                        placeholder="mm/dd/yyyy"
                        size="md"
                      />
                      <DatePickerInput
                        id="date-picker-input-id-finish"
                        labelText="End date"
                        placeholder="mm/dd/yyyy"
                        size="md"
                      />
                    </DatePicker>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <TimePicker
                      id="start-time-picker"
                      labelText="Start time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      size="md"
                    >
                      <TimePickerSelect
                        id="start-time-meridiem"
                        value={startMeridiem}
                        onChange={(e) => setStartMeridiem(e.target.value)}
                      >
                        <SelectItem text="AM" value="AM" />
                        <SelectItem text="PM" value="PM" />
                      </TimePickerSelect>
                    </TimePicker>

                    <TimePicker
                      id="end-time-picker"
                      labelText="End time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      size="md"
                    >
                      <TimePickerSelect
                        id="end-time-meridiem"
                        value={endMeridiem}
                        onChange={(e) => setEndMeridiem(e.target.value)}
                      >
                        <SelectItem text="AM" value="AM" />
                        <SelectItem text="PM" value="PM" />
                      </TimePickerSelect>
                    </TimePicker>
                  </div>
                </div>

                <TextInput
                  id="report-description"
                  labelText="Report Description (optional)"
                  placeholder="e.g. Weekly summary for Warehouse Freezer 1"
                  value={reportDescription}
                  onChange={(e) => setReportDescription(e.target.value)}
                />

                <Checkbox
                  id="enable-access-code"
                  labelText="Enable access code (requires code to view report)"
                  checked={enableAccessCode}
                  onChange={(checked) => setEnableAccessCode(checked)}
                />

                <Button
                  kind="primary"
                  onClick={handleGenerateReport}
                  disabled={isGenerating || !selectedDeviceId || !startDate || !endDate}
                  renderIcon={isGenerating ? Loading : Download}
                >
                  {isGenerating ? 'Generating...' : 'Generate Report'}
                </Button>
              </div>
            </Tile>
          ) : report ? (
            // Report Display
            <>
              {/* Optional inline edit of range for existing report */}
              {isEditingRange && (
                <Tile
                  style={{
                    backgroundColor: bgSecondary,
                    border: `1px solid ${borderColor}`,
                    padding: '16px',
                    marginBottom: '16px',
                    margin: '0 16px 16px 16px',
                  }}
                >
                  <h3 style={{ color: textPrimary, marginBottom: '12px', fontSize: 'clamp(1rem, 4vw, 1.25rem)' }}>Edit Report Range</h3>
                  {generateError && (
                    <InlineNotification
                      kind="error"
                      title="Error"
                      subtitle={generateError}
                      style={{ marginBottom: '12px' }}
                    />
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
                      <div>
                        <DatePicker
                          datePickerType="range"
                          onChange={(dates) => {
                            setStartDate(dates[0] || null);
                            setEndDate(dates[1] || null);
                          }}
                        >
                          <DatePickerInput
                            id="edit-date-picker-input-id-start"
                            labelText="Start date"
                            placeholder="mm/dd/yyyy"
                            size="md"
                          />
                          <DatePickerInput
                            id="edit-date-picker-input-id-finish"
                            labelText="End date"
                            placeholder="mm/dd/yyyy"
                            size="md"
                          />
                        </DatePicker>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <TimePicker
                          id="edit-start-time-picker"
                          labelText="Start time"
                          value={startTime}
                          onChange={(e) => setStartTime(e.target.value)}
                        >
                          <TimePickerSelect
                            id="edit-start-time-meridiem"
                            value={startMeridiem}
                            onChange={(e) => setStartMeridiem(e.target.value)}
                          >
                            <SelectItem text="AM" value="AM" />
                            <SelectItem text="PM" value="PM" />
                          </TimePickerSelect>
                        </TimePicker>

                        <TimePicker
                          id="edit-end-time-picker"
                          labelText="End time"
                          value={endTime}
                          onChange={(e) => setEndTime(e.target.value)}
                        >
                          <TimePickerSelect
                            id="edit-end-time-meridiem"
                            value={endMeridiem}
                            onChange={(e) => setEndMeridiem(e.target.value)}
                          >
                            <SelectItem text="AM" value="AM" />
                            <SelectItem text="PM" value="PM" />
                          </TimePickerSelect>
                        </TimePicker>
                      </div>
                    </div>
                    <TextInput
                      id="edit-report-description"
                      labelText="Report Description (optional)"
                      placeholder="e.g. Weekly summary for Warehouse Freezer 1"
                      value={reportDescription}
                      onChange={(e) => setReportDescription(e.target.value)}
                    />
                    <Checkbox
                      id="edit-enable-access-code"
                      labelText="Enable access code (requires code to view report)"
                      checked={enableAccessCode}
                      onChange={(checked) => setEnableAccessCode(checked)}
                    />
                    <Button
                      kind="primary"
                      onClick={handleUpdateReportRange}
                      disabled={isGenerating || !startDate || !endDate}
                      renderIcon={isGenerating ? Loading : Download}
                    >
                      {isGenerating ? 'Updating...' : 'Update Report'}
                    </Button>
                  </div>
                </Tile>
              )}

              <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '16px', marginBottom: '16px', margin: '0 16px 16px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                    <h2 style={{ color: textPrimary, marginBottom: '8px', fontSize: 'clamp(1.125rem, 4vw, 1.5rem)', wordBreak: 'break-word' }}>
                      {selectedSensor?.name || 'Unknown Device'}
                    </h2>
                    <p style={{ color: textSecondary, fontSize: '0.875rem' }}>
                      {new Date(report.start_date).toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' })} {new Date(report.start_date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true })} - {new Date(report.end_date).toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' })} {new Date(report.end_date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true })}
                    </p>
                    {report.description && (
                      <p style={{ color: textSecondary, fontSize: '0.8rem', marginTop: '4px', wordBreak: 'break-word' }}>
                        {report.description}
                      </p>
                    )}
                  </div>
                  <Tag type="blue" size="sm" style={{ flexShrink: 0 }}>
                    {report.visit_count || 0} visits
                  </Tag>
                </div>

                {/* QR Code and URL */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '16px' }}>
                  <div>
                    <h3 style={{ color: textPrimary, fontSize: '0.875rem', marginBottom: '16px' }}>QR Code</h3>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <div
                        style={{
                          border: `2px solid ${isDark ? '#525252' : '#e0e0e0'}`,
                          borderRadius: '12px',
                          padding: '16px',
                          width: '100%',
                          maxWidth: '280px',
                          backgroundColor: '#ffffff',
                          boxShadow: isDark
                            ? '0 4px 12px rgba(0, 0, 0, 0.3)'
                            : '0 4px 12px rgba(0, 0, 0, 0.08)',
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                          <ReportQRCode
                            publicUrl={report.public_url}
                            storedUrl={report.qr_code_url}
                            size={140}
                          />

                          {/* Device info */}
                          <div style={{ textAlign: 'center', width: '100%' }}>
                            <div
                              style={{
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                color: '#000000',
                                marginBottom: '4px',
                              }}
                            >
                              {selectedSensor?.name || 'Device'}
                            </div>
                            {report.description && (
                              <div style={{ fontSize: '0.75rem', color: '#555555', marginBottom: '4px' }}>
                                {report.description}
                              </div>
                            )}
                            <div style={{ fontSize: '0.65rem', color: '#666666', marginBottom: '8px' }}>
                              {new Date(report.start_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })} {new Date(report.start_date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true })} – {new Date(report.end_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })} {new Date(report.end_date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true })}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#333333', display: 'flex', gap: '16px', justifyContent: 'center' }}>
                              {(stats.tempMin !== null || stats.tempMax !== null || stats.tempAvg !== null) && (
                                <div style={{ textAlign: 'left' }}>
                                  <div style={{ fontWeight: 600, marginBottom: '2px' }}>Temperature</div>
                                  {stats.tempMin !== null && <div>Low: {stats.tempMin.toFixed(1)}°C</div>}
                                  {stats.tempMax !== null && <div>High: {stats.tempMax.toFixed(1)}°C</div>}
                                  {stats.tempAvg !== null && <div>Avg: {stats.tempAvg.toFixed(1)}°C</div>}
                                </div>
                              )}
                              {(stats.humidityMin !== null || stats.humidityMax !== null || stats.humidityAvg !== null) && (
                                <div style={{ textAlign: 'left' }}>
                                  <div style={{ fontWeight: 600, marginBottom: '2px' }}>Humidity</div>
                                  {stats.humidityMin !== null && <div>Low: {stats.humidityMin.toFixed(1)}%</div>}
                                  {stats.humidityMax !== null && <div>High: {stats.humidityMax.toFixed(1)}%</div>}
                                  {stats.humidityAvg !== null && <div>Avg: {stats.humidityAvg.toFixed(1)}%</div>}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* ChillVerify branding */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                            <img
                              src="/chillverify-logo-simple.svg"
                              alt=""
                              style={{
                                width: 16,
                                height: 16,
                                filter: 'invert(25%) sepia(98%) saturate(1653%) hue-rotate(190deg) brightness(93%) contrast(101%)',
                              }}
                            />
                            <span style={{ fontSize: '0.7rem', color: '#0f62fe', fontWeight: 600 }}>ChillVerify</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 style={{ color: textPrimary, fontSize: '0.875rem', marginBottom: '12px' }}>Public URL</h3>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type="text"
                        value={report.public_url}
                        readOnly
                        style={{
                          flex: '1 1 200px',
                          minWidth: '200px',
                          padding: '8px 12px',
                          backgroundColor: bgPrimary,
                          border: `1px solid ${borderColor}`,
                          borderRadius: '4px',
                          color: textPrimary,
                          fontSize: '0.875rem',
                          wordBreak: 'break-all',
                        }}
                      />
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <Button
                          kind="ghost"
                          size="sm"
                          hasIconOnly
                          renderIcon={copied ? Checkmark : Copy}
                          iconDescription="Copy URL"
                          onClick={handleCopyUrl}
                        />
                        <Button
                          kind="secondary"
                          size="sm"
                          renderIcon={Send}
                          onClick={() => setResendModalOpen(true)}
                        >
                          Resend
                        </Button>
                        <Button
                          kind="secondary"
                          size="sm"
                          renderIcon={QrCode}
                          onClick={() => setPrintModalOpen(true)}
                        >
                          Print Label
                        </Button>
                      </div>
                    </div>
                    {report.access_code && (
                      <div style={{ marginTop: '16px' }}>
                        <label style={{ display: 'block', color: textSecondary, fontSize: '0.875rem', marginBottom: '8px' }}>
                          Access Code
                        </label>
                        <TextInput
                          id="access-code-display"
                          value={report.access_code}
                          readOnly
                          style={{ fontFamily: 'monospace', fontWeight: 'bold', letterSpacing: '2px' }}
                        />
                        <p style={{ color: textSecondary, fontSize: '0.75rem', marginTop: '4px' }}>
                          Users must enter this code to view the report
                        </p>
                      </div>
                    )}
                    <p style={{ color: textSecondary, fontSize: '0.75rem', marginTop: '8px' }}>
                      Share this URL or QR code to allow anyone to view this report
                    </p>
                  </div>
                </div>
              </Tile>

              {/* Charts */}
              {report.has_temperature && chartData.tempData.length > 0 && (
                <Tile
                  style={{
                    backgroundColor: bgSecondary,
                    border: `1px solid ${borderColor}`,
                    padding: '16px',
                    marginBottom: '16px',
                    margin: '0 16px 16px 16px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '16px',
                      gap: '16px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <h3
                      style={{
                        color: textPrimary,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        margin: 0,
                      }}
                    >
                      <Temperature size={20} /> Temperature
                    </h3>
                    <div
                      style={{
                        display: 'flex',
                        gap: '12px',
                        fontSize: '0.8rem',
                        color: textSecondary,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span>
                        <strong>Low:</strong>{' '}
                        {stats.tempMin !== null ? `${stats.tempMin.toFixed(1)}${tempUnitLabel}` : '--'}
                      </span>
                      <span>
                        <strong>High:</strong>{' '}
                        {stats.tempMax !== null ? `${stats.tempMax.toFixed(1)}°C` : '--'}
                      </span>
                      <span>
                        <strong>Average:</strong>{' '}
                        {stats.tempAvg !== null ? `${stats.tempAvg.toFixed(1)}°C` : '--'}
                      </span>
                    </div>
                  </div>
                  <SensorChart
                    data={chartData.tempData}
                    label="Temperature"
                    unit={tempUnitLabel}
                    color={REPORT_CHART_COLORS.temperature}
                    timeRange={reportTimeRange}
                    isDark={isDark}
                    onExport={(dataType) => exportReportChartCsv(chartData.tempData, dataType, report?.public_id || 'verify-report')}
                    emptyLabel="No temperature data available for the selected date range"
                  />
                </Tile>
              )}

              {report.has_humidity && chartData.humidityData.length > 0 && (
                <Tile
                  style={{
                    backgroundColor: bgSecondary,
                    border: `1px solid ${borderColor}`,
                    padding: '16px',
                    margin: '0 16px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '16px',
                      gap: '16px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <h3
                      style={{
                        color: textPrimary,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        margin: 0,
                      }}
                    >
                      <Humidity size={20} /> Humidity
                    </h3>
                    <div
                      style={{
                        display: 'flex',
                        gap: '12px',
                        fontSize: '0.8rem',
                        color: textSecondary,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span>
                        <strong>Low:</strong>{' '}
                        {stats.humidityMin !== null ? `${stats.humidityMin.toFixed(1)}%` : '--'}
                      </span>
                      <span>
                        <strong>High:</strong>{' '}
                        {stats.humidityMax !== null ? `${stats.humidityMax.toFixed(1)}%` : '--'}
                      </span>
                      <span>
                        <strong>Average:</strong>{' '}
                        {stats.humidityAvg !== null ? `${stats.humidityAvg.toFixed(1)}%` : '--'}
                      </span>
                    </div>
                  </div>
                  <SensorChart
                    data={chartData.humidityData}
                    label="Humidity"
                    unit="%"
                    color={REPORT_CHART_COLORS.humidity}
                    timeRange={reportTimeRange}
                    isDark={isDark}
                    onExport={(dataType) => exportReportChartCsv(chartData.humidityData, dataType, report?.public_id || 'verify-report')}
                    emptyLabel="No humidity data available for the selected date range"
                  />
                </Tile>
              )}
            </>
          ) : (
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '24px' }}>
              <p style={{ color: textSecondary }}>Report not found</p>
            </Tile>
          )}
        </Column>
      </Grid>

      {/* Resend Modal */}
      <Modal
        open={resendModalOpen}
        onRequestClose={() => setResendModalOpen(false)}
        modalHeading="Send Report via Email"
        primaryButtonText={isSendingEmail ? 'Sending...' : 'Send Email'}
        secondaryButtonText="Cancel"
        onRequestSubmit={handleSendEmail}
        primaryButtonDisabled={isSendingEmail || !resendEmail || emailSuccess}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {emailSuccess && (
            <InlineNotification
              kind="success"
              title="Email Sent!"
              subtitle="The report link has been sent successfully."
              style={{ marginBottom: '16px' }}
            />
          )}
          
          {emailError && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={emailError}
              style={{ marginBottom: '16px' }}
            />
          )}

          <p style={{ color: textPrimary, marginBottom: '8px' }}>
            Enter the email address where you'd like to send the report link. The recipient can click the link or scan the QR code to view the report.
          </p>

          <TextInput
            id="resend-email"
            labelText="Email Address"
            placeholder="recipient@example.com"
            value={resendEmail}
            onChange={(e) => {
              setResendEmail(e.target.value);
              setEmailError(null);
            }}
            disabled={isSendingEmail || emailSuccess}
            invalid={!!emailError}
            invalidText={emailError}
          />

          {report?.public_url && (
            <div style={{ 
              padding: '12px', 
              backgroundColor: bgSecondary, 
              borderRadius: '4px',
              marginTop: '8px'
            }}>
              <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '4px' }}>
                Report URL:
              </p>
              <p style={{ 
                color: textPrimary, 
                fontSize: '0.875rem', 
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                margin: 0
              }}>
                {report.public_url}
              </p>
            </div>
          )}
        </div>
      </Modal>

      {/* Print QR Label Modal */}
      <Modal
        open={printModalOpen}
        onRequestClose={() => setPrintModalOpen(false)}
        modalHeading="Print QR Label"
        primaryButtonText="Print"
        secondaryButtonText="Cancel"
        onRequestSubmit={() => {
          // Trigger browser print; user can choose label printer
          window.print();
        }}
        size="sm"
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
            paddingTop: '8px',
          }}
        >
          {/* Compact label layout suitable for QR sticker */}
          <div
            style={{
              border: '2px solid #e0e0e0',
              borderRadius: '12px',
              padding: '16px',
              width: '280px',
              backgroundColor: '#ffffff',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
              <ReportQRCode
                publicUrl={report?.public_url}
                storedUrl={report?.qr_code_url}
                size={140}
              />

              {/* Device info */}
              <div style={{ textAlign: 'center', width: '100%' }}>
                <div
                  style={{
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#000000',
                    marginBottom: '4px',
                  }}
                >
                  {selectedSensor?.name || 'Device'}
                </div>
                {(reportDescription || report?.description) && (
                  <div style={{ fontSize: '0.75rem', color: '#555555', marginBottom: '4px' }}>
                    {reportDescription || report?.description}
                  </div>
                )}
                {report?.start_date && report?.end_date && (
                  <div style={{ fontSize: '0.6rem', color: '#666666', marginBottom: '8px' }}>
                    {new Date(report.start_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })} {new Date(report.start_date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true })} – {new Date(report.end_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })} {new Date(report.end_date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true })}
                  </div>
                )}
                <div style={{ fontSize: '0.7rem', color: '#333333', display: 'flex', gap: '16px', justifyContent: 'center' }}>
                  {(stats.tempMin !== null || stats.tempMax !== null || stats.tempAvg !== null) && (
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: 600, marginBottom: '2px' }}>Temperature</div>
                      {stats.tempMin !== null && <div>Low: {stats.tempMin.toFixed(1)}°C</div>}
                      {stats.tempMax !== null && <div>High: {stats.tempMax.toFixed(1)}°C</div>}
                      {stats.tempAvg !== null && <div>Avg: {stats.tempAvg.toFixed(1)}°C</div>}
                    </div>
                  )}
                  {(stats.humidityMin !== null || stats.humidityMax !== null || stats.humidityAvg !== null) && (
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: 600, marginBottom: '2px' }}>Humidity</div>
                      {stats.humidityMin !== null && <div>Low: {stats.humidityMin.toFixed(1)}%</div>}
                      {stats.humidityMax !== null && <div>High: {stats.humidityMax.toFixed(1)}%</div>}
                      {stats.humidityAvg !== null && <div>Avg: {stats.humidityAvg.toFixed(1)}%</div>}
                    </div>
                  )}
                </div>
              </div>

              {/* ChillVerify branding */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                <img
                  src="/chillverify-logo-simple.svg"
                  alt=""
                  style={{
                    width: 16,
                    height: 16,
                    filter: 'invert(25%) sepia(98%) saturate(1653%) hue-rotate(190deg) brightness(93%) contrast(101%)',
                  }}
                />
                <span style={{ fontSize: '0.7rem', color: '#0f62fe', fontWeight: 600 }}>ChillVerify</span>
              </div>
            </div>
          </div>

          <p style={{ color: textSecondary, fontSize: '0.75rem', textAlign: 'center' }}>
            Use your browser&apos;s print dialog to print this label on a QR code / label printer.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default VerifyDetailPage;
